import {
  TeContainer,
  TeContainerSlim,
  TeButton,
  TeDivider,
  TeContextMenuItem,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { PlaceholderContainer, RightDrawer } from '@topcon/topcon-utils';

import { useFilterService } from '../../../providers/filter/filter.context';
import { FilterIDs } from '../../../providers/filter/filter.types';
import ViewContainer from '../../../components/ViewContainer';
import TableHeader from '../TableHeader';
import RequestFilters from './RequestFilters';
import RequestsTable from './RequestsTable';
import AcceptRequestModal from './AcceptRequestModal';
import DeclineRequestModal from './DeclineRequestModal';
import { useRequests } from './requestsToJoin.queries';
import { useInvitations, useUsers } from '../userManagement.queries';

function Requests() {
  const [searchValue, setSearchValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [acceptRequestModalOpen, setAcceptRequestModalOpen] = useState(false);
  const [declineRequestModalOpen, setDeclineRequestModalOpen] = useState(false);

  const { data: requests, isFetched: requestsLoaded, refetch } = useRequests();
  const { isFetched: usersLoaded, refetch: refetchUsers } = useUsers();
  const { isFetched: invitationsLoaded, refetch: refetchInvites } = useInvitations();

  const filterID = FilterIDs.RequestFilters;
  const filterService = useFilterService();
  const { t } = useTranslation('users');
  const filters = useMemo(() => {
    return filterService.getFilters(filterID);
  }, [filterID, filterService]);

  const lowercaseSearch = searchValue.toLowerCase();
  // TODO: These need to be reworked once we get proper profiles
  const filteredRequests = useMemo(() => {
    if (!requests) return [];
    return requests.filter(({ identity, createdAt }) => {
      if (!identity) return false;
      const { firstName, lastName, email } = identity;
      const nameMatch = `${firstName} ${lastName}`.toLowerCase().includes(lowercaseSearch);
      const emailMatch = email.toLowerCase().includes(lowercaseSearch);
      const [reqFrom, reqTo] = ['requestedFrom', 'requestedTo'].map((field) => {
        const foundFilter = filters.find(({ name, active }) => active && name === field);
        if (!foundFilter) return undefined;
        return new Date(foundFilter.value as string);
      });

      const requestedAt = new Date(createdAt);
      const fromMatches = !reqFrom || requestedAt >= reqFrom;
      const toMatches = !reqTo || requestedAt <= reqTo;
      return (nameMatch || emailMatch) && fromMatches && toMatches;
    });
  }, [filters, lowercaseSearch, requests]);

  const selectedRequests = useMemo(() => {
    if (!requests) return [];
    return requests.filter(({ id }) => selectedIds.includes(id));
  }, [requests, selectedIds]);

  const loaded = requestsLoaded && usersLoaded && invitationsLoaded;
  const visibleRequests = filteredRequests?.length ?? 0; // TODO: Filters
  const totalRequests = requests?.length ?? 0;
  const selectedRequestsCnt = selectedIds.length;
  const noContent = !loaded || !totalRequests || !visibleRequests;

  const refetchData = () => {
    refetch();
    refetchUsers();
    refetchInvites();
  };

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('loadingRequests')} />;
  };

  const renderNoRequests = () => {
    return <PlaceholderContainer text={t('noRequestsAdded')} icon="groups" />;
  };

  const renderNoContent = () => {
    if (!loaded) return renderLoader();
    if (!totalRequests) return renderNoRequests();
    return <PlaceholderContainer text={t('noSearchResults')} />;
  };

  const renderContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={[0, 0, 4, 4]}
        column
        grow
      >
        {noContent ? (
          renderNoContent()
        ) : (
          <TeContainerSlim fill column>
            <RequestsTable
              requests={filteredRequests}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              setDeclineRequestModalOpen={setDeclineRequestModalOpen}
              setAcceptRequestModalOpen={setAcceptRequestModalOpen}
            />
            <TeContainerSlim
              height={16}
              shadow="top"
              zIndex="base.bottom"
              backgroundColor="background.primary"
              borderRadius={[0, 0, 4, 4]}
              grow
            />
          </TeContainerSlim>
        )}
      </TeContainerSlim>
    );
  };

  const renderActions = () => {
    return (
      <>
        <TeContextMenuItem text={t('accept')} teclick={() => setAcceptRequestModalOpen(true)} />
        <TeContextMenuItem text={t('decline')} teclick={() => setDeclineRequestModalOpen(true)} />
      </>
    );
  };

  const handleRequestAcceptModalClose = (shouldUpdate: boolean) => {
    setAcceptRequestModalOpen(false);
    if (!shouldUpdate) return;
    setSelectedIds([]);
    refetchData();
  };

  const onDeclineRequestModalClose = (shouldUpdate: boolean) => {
    setDeclineRequestModalOpen(false);
    if (!shouldUpdate) return;
    setSelectedIds([]);
    refetchData();
  };

  return (
    <ViewContainer
      title={t('requests')}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      visibleItemsText={
        totalRequests ? t('visibleRequests', { visibleRequests, totalRequests }) : undefined
      }
      searchPlaceholder={t('searchRequests')}
    >
      <TeContainer
        alignItems="center"
        justifyContent="space-between"
        fill
        backgroundColor="background.primary"
        borderRadius={[4, 4, 0, 0]}
        overflow="hidden"
      >
        <TableHeader
          filterID={FilterIDs.RequestFilters}
          filters={filters}
          countText={t('selectedRequests', { count: selectedRequestsCnt })}
          actions={renderActions()}
          actionsDisabled={selectedRequestsCnt < 1}
        />

        <TeContainerSlim alignItems="center" gap="s" justifyContent="flex-start">
          <TeButton
            size="s"
            icon="filter_alt"
            backgroundColor={filtersOpen ? 'background.selected' : 'background.primary'}
            teclick={() => setFiltersOpen(!filtersOpen)}
            iconColor="icon.primary"
          />
        </TeContainerSlim>
      </TeContainer>

      <TeDivider dividerColor="border.secondary" />

      <TeContainerSlim overflow="hidden" column relative grow>
        {renderContent()}
        <RightDrawer open={filtersOpen} slim>
          <RequestFilters requests={requests ?? []} />
        </RightDrawer>
      </TeContainerSlim>

      <AcceptRequestModal
        requests={requests?.filter((r) => selectedIds.includes(r.id)) ?? []}
        open={acceptRequestModalOpen}
        onClose={handleRequestAcceptModalClose}
      />
      <DeclineRequestModal
        open={declineRequestModalOpen}
        selectedRequests={selectedRequests}
        onClose={onDeclineRequestModalClose}
      />
    </ViewContainer>
  );
}

export default Requests;
